/**
 用于管理请求，避免重复请求
 */

const pendingRequests = new Map();

// 白名单配置
let whiteList = [];

/**
 * 生成请求的唯一标识
 * @param {string} url - 请求URL
 * @param {string} method - 请求方法
 * @param {Object|FormData} data - 请求数据
 * @returns {string} - 请求的唯一标识
 */
const generateRequestKey = (url, method, data) => {
  // 对于FormData，我们不包含数据部分，因为它不能被序列化
  const dataStr = data instanceof FormData ? 'FormData' : JSON.stringify(data || {});
  return `${method}:${url}:${dataStr}`;
};

/**
 * 检查请求是否在白名单中
 * @param {string} url - 请求URL
 * @returns {boolean} - 是否在白名单中
 */
const isInWhiteList = (url) => {
  return whiteList.some(pattern => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    } else if (pattern instanceof RegExp) {
      return pattern.test(url);
    }
    return false;
  });
};

/**
 * 如果存在重复请求，则返回现有请求的Promise。
 * @param {string} url - 请求URL
 * @param {string} method - 请求方法
 * @param {Object|FormData} data - 请求数据
 * @returns {Promise|null} - 如果有重复请求，返回它的Promise；否则返回null
 */
const addRequest = (url, method, data) => {
  if (isInWhiteList(url)) {
    return null;
  }
  const key = generateRequestKey(url, method, data);
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key).promise;
  }
  return null;
};

/**
 * 存储新请求
 * @param {string} url
 * @param {string} method
 * @param {Object} data
 * @param {Promise} promise
 * @param {AbortController} controller
 */
const saveRequest = (url, method, data, promise, controller) => {
  if (isInWhiteList(url)) {
    return;
  }
  const key = generateRequestKey(url, method, data);
  pendingRequests.set(key, { promise, controller });
};

/**
 * 移除请求
 * @param {string} url - 请求URL
 * @param {string} method - 请求方法
 * @param {Object|FormData} data - 请求数据
 */
const removeRequest = (url, method, data) => {
  const key = generateRequestKey(url, method, data);
  if (pendingRequests.has(key)) {
    pendingRequests.delete(key);
  }
};

/**
 * 清空所有请求
 */
const clearAllRequests = () => {
  pendingRequests.forEach(req => {
    req.controller?.abort('cancelled');
  });
  pendingRequests.clear();
};

let isUnloading = false;

const markUnloading = () => {
  isUnloading = true;
  clearAllRequests();
};

window.addEventListener('beforeunload', markUnloading);
window.addEventListener('pagehide', markUnloading);
window.addEventListener('pageshow', () => {
  isUnloading = false;
});

const isPageUnloading = () => isUnloading;

/**
 * 设置白名单
 * @param {Array<string|RegExp>} list - 白名单列表，可以是字符串或正则表达式
 */
const setWhiteList = (list) => {
  whiteList = Array.isArray(list) ? list : [];
};

/**
 * 添加白名单项
 * @param {string|RegExp} item - 白名单项，可以是字符串或正则表达式
 */
const addWhiteListItem = (item) => {
  if (!whiteList.includes(item)) {
    whiteList.push(item);
  }
};

/**
 * 移除白名单项
 * @param {string|RegExp} item - 要移除的白名单项
 */
const removeWhiteListItem = (item) => {
  const index = whiteList.findIndex(i => i === item);
  if (index !== -1) {
    whiteList.splice(index, 1);
  }
};

/**
 * 获取当前白名单
 * @returns {Array<string|RegExp>} - 当前白名单
 */
const getWhiteList = () => {
  return [...whiteList];
};

/**
 * 获取当前活跃请求数量
 * @returns {number} - 活跃请求数量
 */
const getPendingRequestsCount = () => {
  return pendingRequests.size;
};

export {
  addRequest,
  saveRequest,
  removeRequest,
  clearAllRequests,
  isPageUnloading,
  setWhiteList,
  addWhiteListItem,
  removeWhiteListItem,
  getWhiteList,
  getPendingRequestsCount
};
